import {
	ASN_PROBLEM,
	parseAsn
} from 'pages/workspace/asset/custom-rules/components/network-values'
import { validTriggerThreshold } from 'pages/workspace/asset/custom-rules/components/rule-options'
import {
	ClickStatus,
	CriteriaModifier,
	CriteriaModifierMode,
	CriteriaModifierType,
	CriteriaType,
	OwnershipType,
	RuleType
} from 'api-models'

import { LevelType } from 'layout/navbar/breadcrumbs/types'
import { FOCUS_OPTIONS, SCOPE_OPTIONS } from '../../constants'
import { RuleFormType } from './types'

export const defaultRuleFormValue = (level: LevelType) => {
	return {
		description: '',
		name: '',
		enabled: true,
		resolve_status: ClickStatus.BAD,
		resolve_labels: { enabled: false, labels: [] },
		block: null,
		temp_ip_exclusion: null,
		audience_exclusion: false,
		trigger: {
			scope: SCOPE_OPTIONS.filter((o) => o.level === level)[0].value,
			scope_values: [],
			max_ad_clicks: undefined,
			period_value: undefined,
			period_type: 'hour',
			focus: FOCUS_OPTIONS[0].value
		},
		modifiers: [],
		threshold: { type: 'all' }
	}
}

export const buildCriteriaModifier = (criteria: string) => {
	switch (criteria) {
		case CriteriaModifierType.NO_DEVICE: {
			return {
				mode: CriteriaModifierMode.POST,
				type: CriteriaModifierType.NO_DEVICE,
				delay: 60
			}
		}
		case CriteriaModifierType.BAD_CLICKS: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.BAD_CLICKS,
				delay: 0
			}
		}
		case CriteriaModifierType.PROXY: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.PROXY,
				delay: 0
			}
		}
		case CriteriaModifierType.THREAT_LEVEL: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.THREAT_LEVEL,
				threat_level: { values: [], negative: false },
				delay: 0
			}
		}
		case CriteriaModifierType.IP_USAGE: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.IP_USAGE,
				ip_usage: { values: [], negative: true },
				delay: 0
			}
		}
		case CriteriaModifierType.DEVICE_TYPE: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.DEVICE_TYPE,
				device_type: { values: [], negative: false },
				delay: 0
			}
		}
		case CriteriaModifierType.ISP:
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.ISP,
				delay: 0,
				isp: { values: [], negative: false }
			}
		case CriteriaModifierType.ASN:
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.ASN,
				delay: 0,
				asn: { values: [], negative: false }
			}
		case CriteriaModifierType.COUNTRY: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.COUNTRY,
				country: { values: [], groups: null, negative: false },
				delay: 0
			}
		}
		case CriteriaModifierType.INTERACTION: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.INTERACTION,
				user_interaction: { values: [], negative: false },
				delay: 600
			}
		}
		case CriteriaModifierType.BOT_PROBABILITY: {
			return {
				mode: CriteriaModifierMode.POST,
				type: CriteriaModifierType.BOT_PROBABILITY,
				bot_probability: { values: [], negative: false },
				delay: 600
			}
		}
		case CriteriaModifierType.TIME: {
			return {
				mode: CriteriaModifierMode.POST,
				type: CriteriaModifierType.TIME,
				time: { primary: { value: 0, unit: 'second' }, negative: true },
				delay: 600
			}
		}
		case CriteriaModifierType.PAGE_VIEWS: {
			return {
				mode: CriteriaModifierMode.POST,
				type: CriteriaModifierType.PAGE_VIEWS,
				page_views: { primary: 0, negative: true },
				delay: 600
			}
		}
		case CriteriaModifierType.CONVERSION: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.CONVERSION,
				conversion: { negative: false, mode: 'all' },
				delay: 0
			}
		}
		case CriteriaModifierType.NOT_CONVERSION: {
			return {
				mode: CriteriaModifierMode.POST,
				type: CriteriaModifierType.NOT_CONVERSION, // this will be changed back to CONVERSION on submit
				conversion: { negative: true },
				delay: 1200
			}
		}
		case CriteriaModifierType.SEARCH_KEYWORD: {
			return {
				mode: CriteriaModifierMode.REALTIME,
				type: CriteriaModifierType.SEARCH_KEYWORD,
				keyword_text: { negative: false, values: [], value: '' },
				delay: 0
			}
		}
	}
}

export const prepareRuleParams = (rule: RuleFormType, level: LevelType) => {
	return {
		name: rule.name,
		description: rule.description,
		ownership: prepareOwnership(level),
		resolve_status: rule.resolve_status,
		summary:
			document
				.getElementById('rule-summary-create')
				?.innerText.replaceAll('\n', ' ') || '',
		focus: { source: rule.trigger.focus, target: null },
		scope: { type: rule.trigger.scope, values: rule.trigger.scope_values },
		actions: {
			block: rule.block ? { period: rule.block.period } : null,
			temp_ip_exclusion: rule.block
				? null
				: rule.temp_ip_exclusion
				? { period: rule.temp_ip_exclusion.period }
				: null,
			audience_exclusion: rule.block ? false : rule.audience_exclusion,
			labels: rule.resolve_labels.enabled
				? rule.resolve_labels.labels?.map((l) => l.trim())
				: null
		},
		criteria: {
			type:
				rule.threshold.type === 'all'
					? CriteriaType.SOURCE_FREQ
					: CriteriaType.SHARE,
			value: Number(rule.trigger.max_ad_clicks),
			share_value: rule.threshold.percentage_value
				? Number(rule.threshold.percentage_value)
				: undefined,
			period: {
				value: rule.trigger.period_value
					? Number(rule.trigger.period_value)
					: undefined,
				unit: rule.trigger.period_type
			},
			modifiers: prepareModifiers(rule.modifiers)
		},
		type: RuleType.CUSTOM
	}
}
const prepareOwnership = (level: LevelType) => {
	if (level === 'website') return OwnershipType.WEBSITE
	if (level === 'ad-account') return OwnershipType.AD_ACCOUNT
	return OwnershipType.CAMPAIGN
}
const prepareModifiers = (modifiers: CriteriaModifier[]) => {
	return modifiers.map((m) => {
		if (m.type === CriteriaModifierType.CONVERSION) {
			const conversion =
				m.conversion?.mode === 'all'
					? { negative: false }
					: { negative: false, label: m.conversion?.label }
			return { ...m, conversion }
		}
		if (m.type === CriteriaModifierType.NOT_CONVERSION) {
			return { ...m, type: CriteriaModifierType.CONVERSION }
		}
		if (m.type === CriteriaModifierType.IP_USAGE) {
			return {
				...m,
				ip_usage: {
					...m.ip_usage,
					values: prepareIpUsageValues(m.ip_usage?.values as string[])
				}
			}
		}
		if (m.type === CriteriaModifierType.TIME) {
			const operand = m.time?.negative ? 'less' : 'more'

			return {
				...m,
				time: { ...m.time, operand }
			}
		}
		if (m.type === CriteriaModifierType.PAGE_VIEWS) {
			const operand = m.page_views?.negative ? 'less' : 'more'

			return {
				...m,
				page_views: { ...m.page_views, operand }
			}
		}
		return m
	})
}
const prepareIpUsageValues = (values: string[]) => {
	return values.map((v: string) => {
		if (v === 'Data center') return 'hosting'
		if (v === 'Business') return 'corporate'
		if (v === 'Residential') return 'consumer'
	})
}

/**
 * Validations
 */
export const validateRule = (
	rule: RuleFormType,
	level: LevelType
): { isValid: boolean; errors: Record<string, string> } => {
	const errors = {} as Record<string, string>

	if (!rule.name) errors['name'] = 'Rule name is required'

	validateScope(rule, level, errors)

	if (!validTriggerThreshold(rule.trigger.max_ad_clicks))
		errors['max_ad_clicks'] = 'Choose a trigger from 1st to 20th click.'
	if (!rule.trigger.period_value)
		errors['period_value'] = 'Period value is required'

	if (rule.modifiers.length > 0) {
		validateThreatLevel(rule.modifiers, errors)
		validateIpUsage(rule.modifiers, errors)
		validateDeviceType(rule.modifiers, errors)
		validateCountry(rule.modifiers, errors)
		const isp = rule.modifiers.find(
			(modifier) => modifier.type === CriteriaModifierType.ISP
		)
		if (
			isp &&
			(!isp.isp?.values.length || isp.isp.values.some((value) => !value.trim()))
		)
			errors.isp = 'Add at least one ISP.'
		const asn = rule.modifiers.find(
			(modifier) => modifier.type === CriteriaModifierType.ASN
		)
		if (
			asn &&
			(!asn.asn?.values.length ||
				asn.asn.values.some((value) => parseAsn(String(value)) === null))
		)
			errors.asn = ASN_PROBLEM
		validateInteraction(rule.modifiers, errors)
		validateBotProbability(rule.modifiers, errors)
		validateTimeSpent(rule.modifiers, errors)
		validatePageViews(rule.modifiers, errors)
		validateSearchKeyword(rule.modifiers, errors)
		validateConversion(rule.modifiers, errors)
	}

	if (rule.resolve_labels.enabled && rule.resolve_labels.labels?.length === 0)
		errors['resolve_labels'] = 'At least one label is required'

	if (rule.block && rule.block.period.value === 0)
		errors['block_period_value'] = 'Required'

	if (rule.temp_ip_exclusion && rule.temp_ip_exclusion.period.value === 0)
		errors['temp_ip_exclusion_period_value'] = 'Required'

	return { isValid: Object.keys(errors).length === 0, errors }
}
const validateScope = (
	rule: RuleFormType,
	level: LevelType,
	errors: Record<string, string>
) => {
	if (
		level === 'website' &&
		rule.trigger.scope === 'ACCOUNT' &&
		rule.trigger.scope_values.length === 0
	) {
		errors['scope_values'] = 'At least one traffic target is required'
	}
	if (
		level === 'ad-account' &&
		rule.trigger.scope === 'CAMPAIGN' &&
		rule.trigger.scope_values.length === 0
	) {
		errors['scope_values'] = 'At least one traffic target is required'
	}
}
const validateThreatLevel = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.THREAT_LEVEL
	)
	if (modifier && modifier.threat_level?.values.length === 0)
		errors['threat_level'] = 'Select a threat level'
}
const validateIpUsage = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.IP_USAGE
	)
	if (modifier && modifier.ip_usage?.values.length === 0)
		errors['ip_usage'] = 'Select an IP address type'
}
const validateDeviceType = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.DEVICE_TYPE
	)
	if (modifier && modifier.device_type?.values.length === 0)
		errors['device_type'] = 'Select a device type'
}
const validateCountry = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.COUNTRY
	)
	if (
		modifier &&
		modifier.country &&
		modifier.country.values &&
		modifier.country.values.length === 0
	)
		errors['country'] = 'Select a country'
}
const validateInteraction = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.INTERACTION
	)
	if (modifier && modifier.user_interaction?.values.length === 0)
		errors['user_interaction'] = 'Select an interaction level'
}
const validateBotProbability = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.BOT_PROBABILITY
	)
	if (modifier && modifier.bot_probability?.values.length === 0)
		errors['bot_probability'] = 'Select a probability level'
}
const validateTimeSpent = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find((m) => m.type === CriteriaModifierType.TIME)
	if (modifier && !modifier.time?.primary.value)
		errors['time'] = 'Time spent in seconds is required'
}
const validatePageViews = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.PAGE_VIEWS
	)
	if (modifier && !modifier.page_views?.primary)
		errors['page_views'] = 'Number of page views is required'
}
const validateSearchKeyword = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.SEARCH_KEYWORD
	)
	const values =
		modifier?.keyword_text?.values ??
		(modifier?.keyword_text?.value ? [modifier.keyword_text.value] : [])
	if (modifier && (!values.length || values.some((value) => !value.trim())))
		errors['keyword_text'] = 'Keyword is required'
}
const validateConversion = (
	modifiers: RuleFormType['modifiers'],
	errors: Record<string, string>
) => {
	const modifier = modifiers.find(
		(m) => m.type === CriteriaModifierType.CONVERSION
	)
	if (
		modifier &&
		modifier.conversion &&
		modifier.conversion.mode === 'with label' &&
		!modifier.conversion.label
	) {
		errors['conversion_label'] = 'Label is required'
	}
}
